<script lang="ts">
  import { createDialog, melt } from "@melt-ui/svelte";
  import type { SvelteHTMLElements } from "svelte/elements";

  import { themeStore } from "$lib/gpu-glossary";
  import type { Theme, ThemeName } from "$lib/gpu-glossary";
  import Markdown from "$lib/ui/common/Markdown.svelte";
  import { scalefade } from "$lib/ui/transitions/scalefade";
  import GlossaryLink from "./GlossaryLink.svelte";

  const {
    elements: { overlay, content, close, portalled },
    states: { open: fullscreen },
  } = createDialog();

  type Props = SvelteHTMLElements["img"];
  let { src, alt }: Props = $props();

  let svgUrl: string | null = $state(null);

  // Images in GPU glossary markdown should be prefixed with "themed-image://"
  const themedImagePrefix = "themed-image://";

  const getImageNameByTheme = (
    imageName: string,
  ): Record<ThemeName, string> => {
    return {
      terminal: `terminal-${imageName}`,
      lightGreen: `light-green-${imageName}`,
      light: `light-${imageName}`,
    };
  };

  function getThemedImageName(theme: Theme, href: string) {
    const imageName = href.slice(themedImagePrefix.length);
    if (!theme) return;
    const imgThemeMap = getImageNameByTheme(imageName);
    const themedImageName = imgThemeMap[theme.name];
    const parsedHref = `https://modal-cdn.com/gpu-glossary/${themedImageName}`;
    svgUrl = parsedHref;
  }

  $effect(() => {
    if (src) {
      getThemedImageName($themeStore, src);
    }
  });
</script>

<figure class="flex flex-col gap-y-2 items-center">
  {#if svgUrl}
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <img
      class="md:max-w-[70%] max-h-[780px]"
      src={svgUrl}
      alt=""
      onclick={() => fullscreen.set(true)}
      onkeypress={() => {}}
    />
    {#if $fullscreen}
      <div
        class="fixed z-40 inset-0 flex flex-col justify-center items-center bg-ground/50"
        use:melt={$portalled}
      >
        <div
          use:melt={$overlay}
          class="absolute inset-0 bg-ground/50 -z-10"
        ></div>
        <img
          src={svgUrl}
          alt=""
          class="max-h-screen cursor-zoom-out bg-ground"
          use:melt={$content}
          use:melt={$close}
          transition:scalefade
        />
      </div>
    {/if}
    {#if alt}
      <figcaption
        class="text-xs md:max-w-[70%] max-h-[780px] text-center italic leading-[1.3]"
        style:font-style={"italic"}
      >
        <Markdown md={alt}>
          {#snippet a(props: SvelteHTMLElements["a"])}
            <GlossaryLink figCaptionSize="sm" {...props} />
          {/snippet}
        </Markdown>
      </figcaption>
    {/if}
  {/if}
</figure>
